import React, { useState } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Select,
  Input,
  Alert,
  Space,
  Typography,
  message,
  Divider,
  Radio
} from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import { inventoryAPI } from '../services/api.ts';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

interface InventoryAdjustmentModalProps {
  visible: boolean;
  stockId: number | null;
  productDescription?: string;
  stockCode?: string;
  availableQuantity: number;
  inProductionQuantity: number;
  inTransitQuantity: number;
  onOk: () => void;
  onCancel: () => void;
}

const InventoryAdjustmentModal: React.FC<InventoryAdjustmentModalProps> = ({
  visible,
  stockId,
  productDescription,
  stockCode,
  availableQuantity,
  inProductionQuantity,
  inTransitQuantity,
  onOk,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState<string>('ADD');
  const [sourceField, setSourceField] = useState<string>('available');

  const handleSubmit = async () => {
    try {
      await form.validateFields();

      if (!stockId) return;

      setLoading(true);

      const values = form.getFieldsValue();

      const adjustmentData = {
        adjustment_type: values.adjustment_type,
        quantity: values.quantity,
        source_field: values.source_field,
        reason: values.reason,
        notes: values.notes
      };

      await inventoryAPI.adjustStock(stockId, adjustmentData);

      message.success('Aggiustamento inventario completato con successo');
      form.resetFields();
      setAdjustmentType('ADD');
      setSourceField('available');
      onOk();
    } catch (error: any) {
      console.error('Errore nell\'aggiustamento inventario:', error);
      message.error(error.response?.data?.detail || 'Errore durante l\'aggiustamento inventario');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAdjustmentType('ADD');
    setSourceField('available');
    onCancel();
  };

  const getCurrentQuantity = () => {
    switch (sourceField) {
      case 'available':
        return availableQuantity;
      case 'in_production':
        return inProductionQuantity;
      case 'in_transit':
        return inTransitQuantity;
      default:
        return 0;
    }
  };

  const getFieldLabel = (field: string) => {
    switch (field) {
      case 'available':
        return 'Disponibile';
      case 'in_production':
        return 'In Produzione';
      case 'in_transit':
        return 'In Transito';
      default:
        return field;
    }
  };

  return (
    <Modal
      title={
        <Space>
          <ToolOutlined />
          <span>Aggiustamento Inventario</span>
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Applica Aggiustamento"
      cancelText="Annulla"
      width={700}
    >
      <Divider />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          message={
            <Space direction="vertical" size="small">
              <Text strong>Prodotto: {productDescription}</Text>
              <Text>Codice Stock: <strong>{stockCode}</strong></Text>
              <Space size="large">
                <Text>Disponibile: <strong>{availableQuantity}</strong></Text>
                <Text>In Produzione: <strong>{inProductionQuantity}</strong></Text>
                <Text>In Transito: <strong>{inTransitQuantity}</strong></Text>
              </Space>
            </Space>
          }
          type="info"
        />

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            adjustment_type: 'ADD',
            source_field: 'available'
          }}
        >
          <Form.Item
            name="adjustment_type"
            label="Tipo di Aggiustamento"
            rules={[{ required: true, message: 'Seleziona il tipo di aggiustamento' }]}
          >
            <Radio.Group onChange={(e) => setAdjustmentType(e.target.value)}>
              <Radio.Button value="ADD">➕ Aggiungi</Radio.Button>
              <Radio.Button value="REMOVE">➖ Rimuovi</Radio.Button>
              <Radio.Button value="SET">🔄 Imposta Valore</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="source_field"
            label="Campo da Modificare"
            rules={[{ required: true, message: 'Seleziona il campo da modificare' }]}
          >
            <Select onChange={(value) => setSourceField(value as string)}>
              <Option value="available">Disponibile (attuale: {availableQuantity})</Option>
              <Option value="in_production">In Produzione (attuale: {inProductionQuantity})</Option>
              <Option value="in_transit">In Transito (attuale: {inTransitQuantity})</Option>
            </Select>
          </Form.Item>

          <Alert
            message={`Quantità corrente in ${getFieldLabel(sourceField)}: ${getCurrentQuantity()}`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="quantity"
            label={
              adjustmentType === 'SET'
                ? 'Nuovo Valore'
                : adjustmentType === 'ADD'
                ? 'Quantità da Aggiungere'
                : 'Quantità da Rimuovere'
            }
            rules={[
              { required: true, message: 'Inserisci la quantità' },
              { type: 'number', min: 0, message: 'La quantità deve essere >= 0' },
              { type: 'number', max: 1000000, message: 'Quantità eccessiva (max 1.000.000)' },
              {
                validator: (_, value) => {
                  if (adjustmentType === 'REMOVE' && value > getCurrentQuantity()) {
                    return Promise.reject(`Non puoi rimuovere più di ${getCurrentQuantity()} unità`);
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              min={0}
              max={1000000}
              style={{ width: '100%' }}
              placeholder={
                adjustmentType === 'SET'
                  ? 'Inserisci il nuovo valore totale'
                  : 'Inserisci la quantità'
              }
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Motivo Aggiustamento"
            rules={[{ required: true, message: 'Il motivo è obbligatorio' }]}
          >
            <Select placeholder="Seleziona un motivo">
              <Option value="Correzione Errore Inventario">Correzione Errore Inventario</Option>
              <Option value="Prodotto Danneggiato">Prodotto Danneggiato</Option>
              <Option value="Prodotto Smarrito">Prodotto Smarrito</Option>
              <Option value="Prodotto Ritrovato">Prodotto Ritrovato</Option>
              <Option value="Rettifica Post Conteggio Fisico">Rettifica Post Conteggio Fisico</Option>
              <Option value="Allineamento Sistema">Allineamento Sistema</Option>
              <Option value="Altro">Altro</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label="Note Aggiuntive (opzionale)"
            rules={[
              { max: 500, message: 'Note troppo lunghe (max 500 caratteri)' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Descrizione dettagliata dell'aggiustamento..."
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>

        <Alert
          message="⚠️ Attenzione"
          description="L'aggiustamento inventario verrà registrato nella storia dei movimenti e non può essere annullato. Verifica attentamente i dati prima di confermare."
          type="warning"
          showIcon
        />
      </Space>
    </Modal>
  );
};

export default InventoryAdjustmentModal;
